import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { logoutUser } from '../store/slices/authSlice';
import { resetGame } from '../store/slices/gameSlice';
import { openGuestSheet } from '../store/slices/uiSlice';

/**
 * Signing out, in one place. `requestSignOut` is what a "Sign out" button
 * calls: an account signs out at once, a guest first sees the warning sheet
 * (their profile cannot be opened again) with "Save my account" next to
 * "Sign out anyway". `performSignOut` is the sign-out itself.
 */
export function useSignOut() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isGuest = useAppSelector(state => state.auth.user?.is_guest === true);

  const performSignOut = useCallback(async (): Promise<void> => {
    try {
      await dispatch(logoutUser()).unwrap();
      dispatch(resetGame());
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }, [dispatch, navigate]);

  const requestSignOut = useCallback(async (): Promise<void> => {
    if (isGuest) {
      dispatch(openGuestSheet('signout'));
      return;
    }
    await performSignOut();
  }, [dispatch, isGuest, performSignOut]);

  return { isGuest, requestSignOut, performSignOut };
}
